import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import ContentLayout from '@layouts/ContentLayout';
import { Box, Button, Divider, Grid, Heading, Tab, TabList, Tabs, VStack, Text } from '@chakra-ui/react';
import { TAB_STATES } from './constants';
import { kebabCaseToSpaceSeperate, camelToSentenceCase, RecursiveObjectRenderer } from '@utils';
import { formatDate } from '@utils/dateUtils';
import { MdOutlineKeyboardDoubleArrowLeft } from 'react-icons/md';
import { AccountsTab } from './AccountsTab';
import { TransfersTab } from './TransfersTab';
import { CorporateBeneficiariesTab } from './CorporateBeneficiariesTab';
import { ScheduledTransfersTab } from './ScheduledTransfersTab';
import { getLibraDashboardConfigs } from '@store/actions/libraDashboardActions';

const LibraDashboard = () => {
  const dispatch = useDispatch();
  const [selectedTab, setSelectedTab] = useState(TAB_STATES.ACCOUNTS);
  const [showRightSidebar, setShowRightSidebar] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemActions, setSelectedItemActions] = useState([]);

  const visibleTabs = Object.values(TAB_STATES);

  useEffect(() => {
    // Fetch libra dashboard configs on mount
    dispatch(getLibraDashboardConfigs());
  }, [dispatch]);

  const handleTabChange = (index) => {
    const newTab = visibleTabs[index];
    setSelectedTab(newTab);
    setSelectedItem(null);
    setSelectedItemActions([]);
  };

  const handleResize = (e) => {
    e?.preventDefault();
    setShowRightSidebar(!showRightSidebar);
  };

  const handleItemClick = (item, actions = []) => {
    setSelectedItem(item);
    setSelectedItemActions(actions);
    setShowRightSidebar(true);
  };

  return (
    <ContentLayout>
      <Grid
        gridTemplateColumns={{
          base: '1fr',
          lg: `${showRightSidebar ? 8.5 : 10}fr 0fr ${showRightSidebar ? 3 : 0}fr`
        }}
      >
        {/* Main Column */}
        <VStack
          overflowY={'auto'}
          overflowX={'hidden'}
          h={{ lg: '90vh' }}
          pt={10}
          pb={4}
          px={{ base: 4, sm: 6, lg: 10 }}
          alignItems={'stretch'}
          gap={4}
        >
          <Heading size="lg" color="colorPrimary">
            Libra Dashboard
          </Heading>
          <Tabs
            variant={'enclosed'}
            size={'md'}
            onChange={handleTabChange}
            defaultIndex={visibleTabs.indexOf(selectedTab)}
          >
            <TabList color={'whiteAlpha.700'} borderColor={'whiteAlpha.500'}>
              {visibleTabs.map((tabState) => (
                <Tab
                  key={tabState}
                  _selected={{
                    color: 'brand.500',
                    border: '1px solid',
                    borderColor: 'whiteAlpha.500',
                    borderBottom: '1px solid black'
                  }}
                  _focus={{
                    boxShadow: 'none'
                  }}
                >
                  {kebabCaseToSpaceSeperate(tabState)}
                </Tab>
              ))}
            </TabList>
          </Tabs>

          {selectedTab === TAB_STATES.ACCOUNTS && (
            <AccountsTab
              selectedItem={selectedItem}
              onItemClick={handleItemClick}
            />
          )}
          {selectedTab === TAB_STATES.TRANSFERS && (
            <TransfersTab
              selectedItem={selectedItem}
              onItemClick={handleItemClick}
            />
          )}
          {selectedTab === TAB_STATES.CORPORATE_BENEFICIARIES && (
            <CorporateBeneficiariesTab
              selectedItem={selectedItem}
              onItemClick={handleItemClick}
            />
          )}
          {selectedTab === TAB_STATES.SCHEDULED_TRANSFERS && (
            <ScheduledTransfersTab
              selectedItem={selectedItem}
              onItemClick={handleItemClick}
            />
          )}
        </VStack>

        {/* Divider with toggle button */}
        <Box position={'relative'}>
          <Divider orientation="vertical" borderColor={'whiteAlpha.500'} />
          <Button
            position={'absolute'}
            top={'10%'}
            left={'-20px'}
            transform={'translateY(-50%)'}
            size={'xs'}
            onClick={handleResize}
            borderRadius={'full'}
            bg={'backGround'}
            border={'2px solid'}
            borderColor={'whiteAlpha.500'}
            _hover={{ bg: 'altGray.400' }}
          >
            <MdOutlineKeyboardDoubleArrowLeft
              style={{
                transform: showRightSidebar ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s'
              }}
            />
          </Button>
        </Box>

        {/* Right Sidebar for details */}
        <VStack
          overflowY={'auto'}
          overflowX={'hidden'}
          h={{ lg: '90vh' }}
          pt={10}
          pb={4}
          px={{ base: 4, sm: 6, lg: 6 }}
          alignItems={'stretch'}
          gap={4}
          display={showRightSidebar ? 'flex' : 'none'}
          maxW="100%"
          flex={1}
        >
          <Heading size="md" color="colorPrimary" mb={2}>
            Details
          </Heading>
          {selectedItem ? (
            <>
              <Box
                bg="altGray.400"
                border="2px solid"
                borderColor="whiteAlpha.500"
                borderRadius="xl"
                p={6}
                maxW="100%"
              >
                <VStack align="stretch" spacing={3} maxW="100%">
                  {Object.entries(selectedItem).map(([key, value]) => {
                    // Render nested objects using RecursiveObjectRenderer
                    if (typeof value === 'object' && value !== null) {
                      return (
                        <Box key={key} maxW="100%" overflowX="hidden">
                          <Text fontSize="sm" color="whiteAlpha.600" mb={1}>
                            {camelToSentenceCase(key)}:
                          </Text>
                          <Box
                            bg="blackAlpha.400"
                            p={2}
                            borderRadius="md"
                          >
                            <RecursiveObjectRenderer value={value} />
                          </Box>
                        </Box>
                      );
                    }

                    // Format date fields using formatDate
                    const isDateField = key === 'createdAt' || key === 'updatedAt' || key === 'stateExecutionTimeoutAt' || key === 'executionTime';
                    const displayValue = isDateField && value ? formatDate(value) : (value?.toString() || '-');

                    return (
                      <Box key={key} maxW="100%" overflowX="hidden">
                        <Text fontSize="sm" color="whiteAlpha.600" mb={1}>
                          {camelToSentenceCase(key)}:
                        </Text>
                        <Text
                          fontSize="sm"
                          color="whiteAlpha.900"
                          wordBreak="break-all"
                          overflowWrap="anywhere"
                          whiteSpace="pre-wrap"
                        >
                          {displayValue}
                        </Text>
                      </Box>
                    );
                  })}
                </VStack>
              </Box>
              {selectedItemActions.length > 0 && (
                <VStack align="stretch" spacing={2} mt={4}>
                  {selectedItemActions}
                </VStack>
              )}
            </>
          ) : (
            <Box
              bg="altGray.400"
              border="2px solid"
              borderColor="whiteAlpha.500"
              borderRadius="xl"
              p={6}
              minH="200px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Text color="whiteAlpha.600" fontSize="sm" textAlign="center">
                Click on any card to view details
              </Text>
            </Box>
          )}
        </VStack>
      </Grid>
    </ContentLayout>
  );
};

export default LibraDashboard;
